import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Building2, FileText, AlertTriangle, CheckCircle2, TrendingUp, Clock, BarChart3, Activity, Shield, AlertOctagon, Loader2 } from 'lucide-react';
import ISYUyariPaneli from './ISYUyariPaneli';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASAMA_COLORS = {
  planlama: '#6b7280', mukayeseli_kesif: '#8b5cf6', ihale_dokumani: '#6366f1',
  ilan: '#3b82f6', teklif_toplama: '#0ea5e9', teklif_degerlendirme: '#06b6d4',
  ihale_karari: '#14b8a6', onay_kesinlesme: '#10b981', sozlesme_imzasi: '#22c55e',
  yer_teslimi: '#c9a227', insaat_baslangic: '#f59e0b', insaat_uygulama: '#f97316',
  hakedisler: '#ef4444', gecici_kabul: '#ec4899', garanti_suresi: '#a855f7',
  kesin_kabul: '#2d7a4f', teminat_iadesi: '#1e3a5f'
};

const TUR_LABELS = { birim_fiyat: 'Birim Fiyat', anahtar_teslim: 'Anahtar Teslim', karma: 'Karma', belirsiz: 'Belirsiz' };
const TUR_COLORS = { birim_fiyat: '#3b82f6', anahtar_teslim: '#8b5cf6', karma: '#f59e0b', belirsiz: '#6b7280' };

const formatMoney = (val) => {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(val);
};

const ASAMA_MAP = {
  planlama: 'Planlama', mukayeseli_kesif: 'Mukayeseli Keşif', ihale_dokumani: 'İhale Dokümanı',
  ilan: 'İlan/Davet', teklif_toplama: 'Teklif Toplama', teklif_degerlendirme: 'Teklif Değerlendirme',
  ihale_karari: 'İhale Kararı', onay_kesinlesme: 'Onay/Kesinleşme', sozlesme_imzasi: 'Sözleşme İmzası',
  yer_teslimi: 'Yer Teslimi', insaat_baslangic: 'İnşaat Başlangıcı', insaat_uygulama: 'İnşaat/Uygulama',
  hakedisler: 'Hakedişler', gecici_kabul: 'Geçici Kabul', garanti_suresi: 'Garanti Süresi',
  kesin_kabul: 'Kesin Kabul', teminat_iadesi: 'Teminat İadesi & Kapanış'
};

export default function ISYDashboard({ ihaleler, loading, onSeedData, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [mevzuatAnalizler, setMevzuatAnalizler] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchMevzuatAnalizler();
  }, [ihaleler]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/isy/dashboard/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchMevzuatAnalizler = async () => {
    try {
      const res = await axios.get(`${API}/isy/mevzuat-analiz-son`, { withCredentials: true });
      setMevzuatAnalizler(res.data || []);
    } catch (e) { console.error(e); }
  };

  if (loading || !stats) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Yükleniyor...</div></div>;
  }

  const pieData = Object.entries(stats.tur_dagilimi || {}).map(([key, val]) => ({
    name: TUR_LABELS[key] || key, value: val, color: TUR_COLORS[key] || '#6b7280'
  }));

  const asamaPipelineData = Object.entries(ASAMA_MAP).map(([kod, ad]) => ({
    kod, ad, count: stats.asama_dagilimi?.[kod] || 0, color: ASAMA_COLORS[kod]
  }));

  return (
    <div className="space-y-6" data-testid="isy-dashboard">
      {stats.toplam === 0 && (
        <Card className="bg-amber-900/20 border-amber-700/50">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-200 text-sm">Henüz ihale kaydı yok. Örnek veriler yüklemek ister misiniz?</span>
            </div>
            <Button onClick={onSeedData} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="seed-data-btn">
              Örnek Veri Yükle
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Asistan Uyarı Paneli — tüm ihaleler süre/iş artışı riskine göre taranır */}
      <ISYUyariPaneli onNavigate={onNavigate} refreshKey={(ihaleler || []).length} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-900/80 border-gray-700/50 hover:border-blue-500/30 transition-colors cursor-pointer" onClick={() => onNavigate('ihaleler')}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              <span className="text-2xl font-bold text-white">{stats.toplam}</span>
            </div>
            <p className="text-xs text-gray-400">Toplam İhale</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-5 h-5 text-amber-400" />
              <span className="text-2xl font-bold text-white">{stats.ihale_mod}</span>
            </div>
            <p className="text-xs text-gray-400">İhale Sürecinde</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-2xl font-bold text-white">{stats.sozlesme_mod}</span>
            </div>
            <p className="text-xs text-gray-400">Sözleşme Sürecinde</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span className="text-2xl font-bold text-white">{stats.tamamlanan}</span>
            </div>
            <p className="text-xs text-gray-400">Tamamlanan</p>
          </CardContent>
        </Card>
      </div>

      {/* Mali Özet */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Toplam Yaklaşık Maliyet</p>
            <p className="text-lg font-bold text-blue-400">{formatMoney(stats.toplam_yaklasik_maliyet)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Toplam Sözleşme Bedeli</p>
            <p className="text-lg font-bold text-amber-400">{formatMoney(stats.toplam_sozlesme_bedeli)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Toplam Ödenen Hakediş</p>
            <p className="text-lg font-bold text-green-400">{formatMoney(stats.toplam_hakedis)}</p>
            {mevzuatAnalizler.length > 0 && (
              <p className="text-[10px] text-emerald-600 mt-0.5">Mevzuat denetiminden senkronize</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sözleşme Türü Dağılımı */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300">Sözleşme Türü Dağılımı</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(val) => [val, 'Adet']} contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                      <span className="text-xs text-gray-300">{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">Veri yok</p>
            )}
          </CardContent>
        </Card>

        {/* İş Artışı Risk Göstergesi */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" /> İş Artışı Risk Göstergesi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.is_artisi_risk?.length > 0 ? (
              <div className="space-y-3">
                {stats.is_artisi_risk.map((r, i) => (
                  <div key={i} className="p-2 bg-red-900/20 border border-red-800/30 rounded-lg">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-300 truncate max-w-[200px]">{r.is_adi}</span>
                      <Badge variant="destructive" className="text-xs">%{r.oran?.toFixed(1)} / %{r.sinir}</Badge>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all" style={{
                        width: `${Math.min((r.oran / r.sinir) * 100, 100)}%`,
                        background: r.oran / r.sinir > 0.9 ? '#ef4444' : r.oran / r.sinir > 0.7 ? '#f59e0b' : '#22c55e'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">Risk yok</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mevzuat Denetim Sonuçları Widget */}
      {mevzuatAnalizler.length > 0 && (
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
              <Shield className="w-4 h-4 text-cyan-400" /> Mevzuat Denetim Sonuçları
              <Badge className="bg-gray-800 text-gray-400 text-[10px] ml-auto">{mevzuatAnalizler.length} ihale analiz edildi</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {mevzuatAnalizler.map(a => {
                const risk = a.analiz?.risk_level || 'belirsiz';
                const riskColor = risk === 'kritik' ? '#ef4444' : risk === 'yuksek' ? '#f97316' : risk === 'orta' ? '#f59e0b' : risk === 'dusuk' ? '#22c55e' : '#6b7280';
                const riskLabel = risk === 'kritik' ? 'KRİTİK' : risk === 'yuksek' ? 'YÜKSEK' : risk === 'orta' ? 'ORTA' : risk === 'dusuk' ? 'DÜŞÜK' : 'BELİRSİZ';
                const riskBg = risk === 'kritik' || risk === 'yuksek' ? 'bg-red-900/15' : risk === 'orta' ? 'bg-amber-900/15' : 'bg-green-900/15';
                const ihale = (ihaleler || []).find(ih => ih.id === a.ihale_id);
                const warnCount = a.analiz?.warnings?.length || 0;
                return (
                  <div key={a.id} className={`${riskBg} rounded-lg p-3 border border-gray-800/40`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Shield className="w-4 h-4 flex-shrink-0" style={{ color: riskColor }} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{ihale?.is_adi || a.dosya_adi}</p>
                          <p className="text-[10px] text-gray-500">{a.dosya_adi} | {warnCount} uyarı | {a.created_at ? new Date(a.created_at).toLocaleDateString('tr-TR') : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge style={{ background: riskColor + '22', color: riskColor, border: `1px solid ${riskColor}44` }} className="text-[10px] px-2 py-0.5">
                          {riskLabel} RİSK
                        </Badge>
                        {a.analiz?.sinir_asimi && (
                          <Badge className="bg-red-700 text-white text-[10px]">SINIR AŞIMI</Badge>
                        )}
                      </div>
                    </div>
                    {a.analiz?.ozet && (
                      <p className="text-[11px] text-gray-400 mt-1.5">{a.analiz.ozet}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sağlık Raporu */}
      {(stats.saglik_raporu || []).length > 0 && (
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400" /> İhale Sağlık Raporu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(stats.saglik_raporu || []).map(r => {
                const skorColor = r.durum === 'iyi' ? '#22c55e' : r.durum === 'dikkat' ? '#f59e0b' : r.durum === 'risk' ? '#f97316' : '#ef4444';
                const skorBg = r.durum === 'iyi' ? 'bg-green-900/15' : r.durum === 'dikkat' ? 'bg-amber-900/15' : r.durum === 'risk' ? 'bg-orange-900/15' : 'bg-red-900/15';
                const durumLabel = r.durum === 'iyi' ? 'İyi' : r.durum === 'dikkat' ? 'Dikkat' : r.durum === 'risk' ? 'Riskli' : 'Kritik';
                const DurumIcon = r.durum === 'iyi' ? CheckCircle2 : r.durum === 'dikkat' ? AlertTriangle : r.durum === 'risk' ? AlertTriangle : AlertOctagon;
                const asamaLabel = ASAMA_MAP[r.mevcut_asama] || r.mevcut_asama;
                return (
                  <div key={r.id} className={`${skorBg} rounded-lg p-3 border border-gray-800/40`} data-testid={`saglik-${r.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <DurumIcon className="w-5 h-5 flex-shrink-0" style={{ color: skorColor }} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{r.is_adi}</p>
                          <p className="text-[10px] text-gray-500">{asamaLabel}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge style={{ background: skorColor + '22', color: skorColor, border: `1px solid ${skorColor}44` }} className="text-xs px-2 py-0.5">
                          {durumLabel}
                        </Badge>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center border-2" style={{ borderColor: skorColor }}>
                          <span className="text-sm font-bold" style={{ color: skorColor }}>{r.toplam_skor}</span>
                        </div>
                      </div>
                    </div>
                    {/* Alt Puanlar */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      <div className="flex items-center gap-1.5">
                        <Shield className="w-3 h-3 text-blue-400" />
                        <span className="text-[10px] text-gray-400">Uyum:</span>
                        <span className={`text-[10px] font-semibold ${r.uyum_puani !== null ? (r.uyum_puani >= 70 ? 'text-green-400' : r.uyum_puani >= 50 ? 'text-amber-400' : 'text-red-400') : 'text-gray-600'}`}>
                          {r.uyum_puani !== null ? `%${r.uyum_puani}` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-amber-400" />
                        <span className="text-[10px] text-gray-400">Gecikme:</span>
                        <span className={`text-[10px] font-semibold ${r.gecikme_gun === 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {r.gecikme_gun > 0 ? `${r.gecikme_gun} gün` : 'Yok'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3 text-red-400" />
                        <span className="text-[10px] text-gray-400">Eksiklik:</span>
                        <span className={`text-[10px] font-semibold ${r.acik_eksiklik === 0 ? 'text-green-400' : r.kritik_eksiklik > 0 ? 'text-red-400' : 'text-amber-400'}`}>
                          {r.acik_eksiklik > 0 ? `${r.acik_eksiklik} açık${r.kritik_eksiklik > 0 ? ` (${r.kritik_eksiklik} kritik)` : ''}` : 'Yok'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-3 h-3 text-purple-400" />
                        <span className="text-[10px] text-gray-400">İş Artışı:</span>
                        <span className={`text-[10px] font-semibold ${r.is_artisi_orani === 0 ? 'text-green-400' : r.is_artisi_puani < 30 ? 'text-red-400' : 'text-amber-400'}`}>
                          {r.is_artisi_orani > 0 ? `%${r.is_artisi_orani.toFixed(1)}` : 'Yok'}
                        </span>
                      </div>
                    </div>
                    {/* Skor Barı */}
                    <div className="mt-2 w-full bg-gray-800 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${r.toplam_skor}%`, background: skorColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aşama Pipeline */}
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-300 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-400" /> Aşama Dağılımı
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {/* İhale Süreci */}
            <p className="text-xs text-gray-500 font-semibold mt-2 mb-1">İHALE SÜRECİ</p>
            {asamaPipelineData.filter(a => ['planlama','mukayeseli_kesif','ihale_dokumani','ilan','teklif_toplama','teklif_degerlendirme','ihale_karari','onay_kesinlesme','sozlesme_imzasi'].includes(a.kod)).map(a => (
              <div key={a.kod} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-36 truncate">{a.ad}</span>
                <div className="flex-1 bg-gray-800 rounded-full h-4 relative">
                  <div className="h-4 rounded-full flex items-center justify-end pr-2 transition-all" style={{
                    width: `${Math.max(a.count > 0 ? (a.count / Math.max(stats.toplam, 1)) * 100 : 0, a.count > 0 ? 15 : 0)}%`,
                    background: a.color, minWidth: a.count > 0 ? '28px' : '0'
                  }}>
                    {a.count > 0 && <span className="text-[10px] text-white font-bold">{a.count}</span>}
                  </div>
                </div>
              </div>
            ))}
            {/* Sözleşme Süreci */}
            <p className="text-xs text-gray-500 font-semibold mt-3 mb-1">SÖZLEŞME SÜRECİ</p>
            {asamaPipelineData.filter(a => ['yer_teslimi','insaat_baslangic','insaat_uygulama','hakedisler','gecici_kabul','garanti_suresi','kesin_kabul','teminat_iadesi'].includes(a.kod)).map(a => (
              <div key={a.kod} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-36 truncate">{a.ad}</span>
                <div className="flex-1 bg-gray-800 rounded-full h-4 relative">
                  <div className="h-4 rounded-full flex items-center justify-end pr-2 transition-all" style={{
                    width: `${Math.max(a.count > 0 ? (a.count / Math.max(stats.toplam, 1)) * 100 : 0, a.count > 0 ? 15 : 0)}%`,
                    background: a.color, minWidth: a.count > 0 ? '28px' : '0'
                  }}>
                    {a.count > 0 && <span className="text-[10px] text-white font-bold">{a.count}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Hızlı Aksiyonlar */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onNavigate('ihaleler')} size="sm" className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="quick-new-ihale">
          Yeni İhale Başlat
        </Button>
        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" onClick={() => onNavigate('oska_amp')}>
          <Clock className="w-3.5 h-3.5 mr-1" /> OSKA/AMP Veri Yükle
        </Button>
        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" onClick={() => onNavigate('oska_amp')}>
          Mevzuat Taraması
        </Button>
      </div>
    </div>
  );
}
